import { SlugHelper } from './slug-helper';
import { assertIsNonEmptyString, assertIsNotNull, assertIsObject } from '../../lib/test-and-assert/assert-base';
import { formatting } from '../../qaamgo/helper/formatting';
import { paymentApi } from './payment-api';
import { testIsNonEmptyString, testIsUndefined } from '../../lib/test-and-assert/test-base';
import { stripeHelper } from './stripe-helper';
import { globalLogger } from '../../qaamgo/helper/global-logger';
import { projectHelper } from '../misc/project-helper';
import { translate } from '../../qaamgo/helper/translate';

class PaymentManager {
    constructor() {
        this._initializedPricingInformation = false;
        this._slugHelper = new SlugHelper();
        this._trialData = {};
        this._largestPurchasableFileSize = '';
        this._freeItems = null;

        /**
         * @type {smallestPricesDto}
         */
        this._smallestPricesPerMonth = {};
    }

    setConfigFromPricingInformation(pricingInformation) {
        this._trialData = pricingInformation.free_trial_data;
        this._freeItems = pricingInformation.free_items;
        this._smallestPricesPerMonth = pricingInformation.smallest_prices_per_month;
        this._largestPurchasableFileSize = formatting.fileSize(pricingInformation.largest_purchasable_file_size_bytes);

        // fallback in case this info wasn't returned by the satcore for some reason
        if (!testIsNonEmptyString(this._largestPurchasableFileSize)) {
            /**
             *  online-convert - video2edit 8GB
             *  ebook2edit - audio2edit - ocr2edit 1GB
             *  All others 4GB   
             */
            let largestFileSizeGb = '4';
            
            if (
                projectHelper.isOnlineConvert() ||
                projectHelper.isVideo2edit()
            ) {
                largestFileSizeGb = '8';
            }

            if (
                projectHelper.isEbook2edit() ||
                projectHelper.isAudio2Edit() ||
                projectHelper.isOcr2edit()
            ) {
                largestFileSizeGb = '1';
            }

            this._largestPurchasableFileSize = translate('%1 GB', { '%1': largestFileSizeGb });
        }

        this._slugHelper.setSlugData(pricingInformation.pricing_data);

        this._initializedPricingInformation = true;
    }

    isPricingInfoInitialized() {
        return this._initializedPricingInformation;
    }

    getLargestPurchasableFileSize() {
        return this._largestPurchasableFileSize;
    }

    getTrialData() {
        return this._trialData;
    }

    getFreeItems() {
        return this._freeItems;
    }

    /**
     * @return {smallestPricesDto}
     */
    getSmallestPricesPerMonth() {
        return this._smallestPricesPerMonth;
    }

    /**
     * @return {SlugHelper}
     */
    getSlugHelper() {
        return this._slugHelper;
    }

    handlePaymentPaypalSubscription(slug, user = 0) {
        // subscription flow:
        // - POST agreement approval
        // - redirect to approval_link

        return paymentApi.postAgreementApproval('paypal', slug, user).then((agreementApproval) => {
            return Promise.resolve(agreementApproval.approval_link);
        });
    }

    handlePaymentPaypalOneTimePurchase(slug, user = 0) {
        // single purchase flow:
        // - POST order
        // - redirect to order_response

        return paymentApi.postOrder('paypal', slug, user).then((orderResponse) => {
            return Promise.resolve(orderResponse);
        });
    }

    createStripeSubscriptionPaymentIntent(slug, user = 0) {
        // create a not active subscription to get a payment_intent to initialize the stripe elements
        // if then payment completes the subscription is activated
        return paymentApi.postAgreement('stripe', slug, user)
            .then((response) => {
                let intent = response.data.data?.latest_invoice?.payment_intent;

                const paymentIntentSecret = intent?.client_secret;

                if (testIsUndefined(paymentIntentSecret)) {
                    globalLogger.log(
                        'payment manager - createStripeSubscriptionPaymentIntent :: undefined payment intent secret'
                    );

                    return Promise.reject();
                }

                return Promise.resolve(paymentIntentSecret);
            });
    }

    createStripeOrderPaymentIntent(slug, user = 0) {
        // create an order and get the stripe payment_intent to initialize stripe elements
        return paymentApi
            .postOrder('stripe', slug, user)
            .then((orderResponse) => {
                return orderResponse;
            });
    }
}

if (!global.gPaymentManager) {
    global.gPaymentManager = new PaymentManager();
}

const paymentManager = global.gPaymentManager;

export { paymentManager };
